"use client"

import { ANNOUNCEMENT_LEVELS, MAX_ANNOUNCEMENTS, MENU_ROLES, type Announcement, type AnnouncementLevel, type Maintenance, type MenuRole } from "@repo/types"
import { Pencil, Plus, Trash2 } from "lucide-react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { DendromapLoader } from "../../components/dendromap-loader"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog"
import { Button } from "../../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { Checkbox } from "../../components/ui/checkbox"
import { StatusBadge } from "../../components/ui/data-table/status-badge"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import { Switch } from "../../components/ui/switch"
import { Textarea } from "../../components/ui/textarea"
import { useAnnouncementsAdmin, useMaintenanceAdmin, usePlatformAdminActions } from "../../hooks/api/platform/use-platform"
import { useLabels } from "../../hooks/use-labels"
import { PageHeader } from "../../ui/layout/page-header"
import { apiErrorMessage, formatDate } from "../superadmin/client-detail.helpers"
import { announcementState, isoToLocalInput, localInputToIso, newAnnouncement } from "./platform.helpers"

const LEVEL_LABELS: Record<AnnouncementLevel, string> = { info: "Information", warning: "Avertissement", critical: "Critique" }
const ROLE_LABELS: Record<MenuRole, string> = {
  ADMIN: "Admin",
  DENDROMAP: "Dendromap",
  MANAGER: "Manager",
  AGENT: "Agent",
  USER: "Utilisateur",
  VISITEUR: "Visiteur",
}
const STATE_BADGES = {
  active: { tone: "active", label: "En cours" },
  scheduled: { tone: "trial", label: "Programmée" },
  ended: { tone: "inactive", label: "Terminée" },
} as const

/** Espace /superadmin : mode maintenance et annonces affichées dans l'application. */
export function AnnouncementsPage() {
  const { labels } = useLabels()
  return (
    <div className="space-y-6">
      <PageHeader>
        <PageHeader.Title>{labels.sidebar.superadmin.announcements}</PageHeader.Title>
        <PageHeader.Description>
          Bandeaux affichés en haut du dashboard, et mode maintenance qui bloque l&apos;application pour tous sauf les superadmins.
        </PageHeader.Description>
      </PageHeader>
      <MaintenanceCard />
      <AnnouncementsCard />
    </div>
  )
}

function MaintenanceCard() {
  const { data: saved, isLoading } = useMaintenanceAdmin()
  const { saveMaintenance } = usePlatformAdminActions()
  const [draft, setDraft] = useState<Maintenance>({ enabled: false, message: "", ends_at: null })
  const [confirmOpen, setConfirmOpen] = useState(false)

  useEffect(() => {
    if (saved) setDraft(saved)
  }, [saved])

  if (isLoading) return <DendromapLoader message="Chargement..." />

  const save = async () => {
    try {
      await saveMaintenance.mutateAsync(draft)
      toast.success(draft.enabled ? "Maintenance activée" : "Maintenance enregistrée")
    } catch (error) {
      toast.error(apiErrorMessage(error, "Enregistrement impossible"))
    }
  }
  const turningOn = draft.enabled && !saved?.enabled

  return (
    <Card className={saved?.enabled ? "border-destructive" : undefined}>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">Mode maintenance</CardTitle>
            <CardDescription>
              {saved?.enabled
                ? "Actif : seuls les superadmins peuvent utiliser l'application."
                : "Inactif. Une fois activé, l'API refuse les requêtes des utilisateurs et le dashboard affiche une page de maintenance."}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="maintenance-enabled">Activer</Label>
            <Switch id="maintenance-enabled" checked={draft.enabled} onCheckedChange={(enabled) => setDraft({ ...draft, enabled })} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="maintenance-message">Message affiché</Label>
          <Textarea
            id="maintenance-message"
            value={draft.message}
            maxLength={500}
            placeholder="Ex. : mise à jour en cours, retour prévu vers 23 h."
            onChange={(e) => setDraft({ ...draft, message: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="maintenance-ends">Fin prévue (indicative)</Label>
          <Input
            id="maintenance-ends"
            type="datetime-local"
            className="w-60"
            value={isoToLocalInput(draft.ends_at)}
            onChange={(e) => setDraft({ ...draft, ends_at: localInputToIso(e.target.value) })}
          />
        </div>
        <div className="flex justify-end">
          <Button
            variant={turningOn ? "destructive" : "default"}
            disabled={saveMaintenance.isPending}
            onClick={() => (turningOn ? setConfirmOpen(true) : save())}
          >
            {turningOn ? "Activer la maintenance" : "Enregistrer"}
          </Button>
        </div>
      </CardContent>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Activer la maintenance ?</AlertDialogTitle>
            <AlertDialogDescription>
              Tous les utilisateurs (dashboard et app mobile) seront bloqués dans les secondes qui suivent, sauf les superadmins.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={save}>Activer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}

function AnnouncementsCard() {
  const { data: announcements = [], isLoading } = useAnnouncementsAdmin()
  const { saveAnnouncements } = usePlatformAdminActions()
  const [editing, setEditing] = useState<Announcement | null>(null)

  const persist = async (next: Announcement[], success: string) => {
    try {
      await saveAnnouncements.mutateAsync(next)
      toast.success(success)
      return true
    } catch (error) {
      toast.error(apiErrorMessage(error, "Enregistrement impossible"))
      return false
    }
  }

  const submit = async (announcement: Announcement) => {
    const stamped = { ...announcement, updated_at: new Date().toISOString() }
    const exists = announcements.some((a) => a.id === stamped.id)
    const next = exists ? announcements.map((a) => (a.id === stamped.id ? stamped : a)) : [...announcements, stamped]
    if (await persist(next, exists ? "Annonce modifiée" : "Annonce publiée")) setEditing(null)
  }

  if (isLoading) return <DendromapLoader message="Chargement des annonces..." />

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">Annonces</CardTitle>
            <CardDescription>{announcements.length} / {MAX_ANNOUNCEMENTS} annonces.</CardDescription>
          </div>
          <Button size="sm" disabled={announcements.length >= MAX_ANNOUNCEMENTS} onClick={() => setEditing(newAnnouncement())}>
            <Plus className="mr-2 h-4 w-4" /> Nouvelle annonce
          </Button>
        </div>
      </CardHeader>
      <CardContent className="divide-y divide-border">
        {announcements.length === 0 && <p className="text-sm text-muted-foreground">Aucune annonce.</p>}
        {announcements.map((announcement) => {
          const state = STATE_BADGES[announcementState(announcement)]
          return (
            <div key={announcement.id} className="flex flex-wrap items-start gap-3 py-3">
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge tone={state.tone} label={state.label} />
                  <span className="text-xs text-muted-foreground">
                    {LEVEL_LABELS[announcement.level]} ·{" "}
                    {announcement.roles.length ? announcement.roles.map((r) => ROLE_LABELS[r]).join(", ") : "Tout le monde"}
                    {announcement.starts_at || announcement.ends_at
                      ? ` · ${formatDate(announcement.starts_at)} → ${formatDate(announcement.ends_at)}`
                      : ""}
                  </span>
                </div>
                <p className="text-sm text-foreground whitespace-pre-line">{announcement.message}</p>
              </div>
              <Button variant="ghost" size="sm" aria-label="Modifier" onClick={() => setEditing(announcement)}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                aria-label="Supprimer"
                disabled={saveAnnouncements.isPending}
                onClick={() => persist(announcements.filter((a) => a.id !== announcement.id), "Annonce supprimée")}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          )
        })}
      </CardContent>
      {editing && (
        <AnnouncementDialog
          announcement={editing}
          isNew={!announcements.some((a) => a.id === editing.id)}
          pending={saveAnnouncements.isPending}
          onCancel={() => setEditing(null)}
          onSubmit={submit}
        />
      )}
    </Card>
  )
}

function AnnouncementDialog({
  announcement,
  isNew,
  pending,
  onCancel,
  onSubmit,
}: {
  announcement: Announcement
  isNew: boolean
  pending: boolean
  onCancel: () => void
  onSubmit: (announcement: Announcement) => void
}) {
  const [draft, setDraft] = useState(announcement)
  const toggleRole = (role: MenuRole, checked: boolean) =>
    setDraft({ ...draft, roles: checked ? [...draft.roles, role] : draft.roles.filter((r) => r !== role) })

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nouvelle annonce" : "Modifier l'annonce"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="announcement-message">Message</Label>
            <Textarea id="announcement-message" value={draft.message} maxLength={500} onChange={(e) => setDraft({ ...draft, message: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Niveau</Label>
            <Select value={draft.level} onValueChange={(level) => setDraft({ ...draft, level: level as AnnouncementLevel })}>
              <SelectTrigger className="w-48" aria-label="Niveau"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ANNOUNCEMENT_LEVELS.map((level) => <SelectItem key={level} value={level}>{LEVEL_LABELS[level]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Visible par (aucun = tout le monde)</Label>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {MENU_ROLES.map((role) => (
                <label key={role} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={draft.roles.includes(role)} onCheckedChange={(checked) => toggleRole(role, checked === true)} />
                  {ROLE_LABELS[role]}
                </label>
              ))}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="announcement-start">Début (facultatif)</Label>
              <Input id="announcement-start" type="datetime-local" value={isoToLocalInput(draft.starts_at)} onChange={(e) => setDraft({ ...draft, starts_at: localInputToIso(e.target.value) })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="announcement-end">Fin (facultatif)</Label>
              <Input id="announcement-end" type="datetime-local" value={isoToLocalInput(draft.ends_at)} onChange={(e) => setDraft({ ...draft, ends_at: localInputToIso(e.target.value) })} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={draft.dismissible} onCheckedChange={(dismissible) => setDraft({ ...draft, dismissible })} />
            L&apos;utilisateur peut la masquer
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Annuler</Button>
          <Button disabled={pending || draft.message.trim().length < 3} onClick={() => onSubmit({ ...draft, message: draft.message.trim() })}>
            {isNew ? "Publier" : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
